import { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/ToastContext';
import {
  createGuestFinancePayment,
  fetchGuestPayment,
  fetchGuestPaymentReceipt,
  fetchGuestPayments,
  mockCaptureGuestPayment,
} from '../../api/guestApi';
import { listGuestInvoices, openInvoicePdf } from '../../api/invoices';

const tabs = [
  { key: 'pending', label: 'Pending', statuses: ['created', 'authorized', 'pending', 'initiated'] },
  { key: 'paid', label: 'Paid', statuses: ['captured', 'paid'] },
  { key: 'failed', label: 'Failed/Refunded', statuses: ['failed', 'refunded', 'partially_refunded'] },
];

const badgeStyles = {
  created: 'bg-amber-100 text-amber-700',
  authorized: 'bg-amber-100 text-amber-700',
  pending: 'bg-amber-100 text-amber-700',
  initiated: 'bg-amber-100 text-amber-700',
  captured: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  refunded: 'bg-rose-100 text-rose-700',
  partially_refunded: 'bg-rose-100 text-rose-700',
  default: 'bg-slate-100 text-slate-700',
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = badgeStyles[key] || badgeStyles.default;
  const label = String(value || 'unknown').replace(/_/g, ' ');
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${cls}`}>{label}</span>;
};

const formatDateTime = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return 'TBD';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatAmount = (currency, amount, amountPaise) => {
  const paise = Number(amountPaise);
  if (Number.isFinite(paise) && amountPaise !== null && amountPaise !== undefined) {
    const value = paise / 100;
    return `${currency || ''} ${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
  }
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return `${currency || ''} ${amount || ''}`.trim();
  return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const canRetryPayment = (status) => {
  const value = String(status || '').toLowerCase();
  return ['created', 'authorized', 'pending', 'failed', 'initiated'].includes(value);
};

const canCapturePayment = (status) => {
  const value = String(status || '').toLowerCase();
  return ['created', 'authorized', 'pending', 'initiated'].includes(value);
};

const GuestPayments = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async (nextPage = page, tabKey = activeTab) => {
    const tab = tabs.find((t) => t.key === tabKey) || tabs[0];
    setLoading(true);
    setError('');
    try {
      const res = await fetchGuestPayments({
        status: tab.statuses.join(','),
        page: nextPage,
        limit: pagination.limit || 10,
      });
      setItems(res.data || []);
      setPagination(res.pagination || { page: nextPage, totalPages: 1, total: 0, limit: pagination.limit || 10 });
    } catch (err) {
      setError(err.message || 'Unable to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeTab]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
  };

  const openDetail = async (paymentId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelected(null);
    try {
      const res = await fetchGuestPayment(paymentId);
      setSelected(res);
    } catch (err) {
      toast.error(err.message || 'Unable to load payment detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelected(null);
  };

  const handleRetry = async (payment) => {
    if (!payment?.booking_id) return;
    setActionId(payment.id);
    try {
      await createGuestFinancePayment(payment.booking_id);
      toast.success('Payment request created');
      await load(page, activeTab);
    } catch (err) {
      toast.error(err.message || 'Payment retry failed');
    } finally {
      setActionId(null);
    }
  };

  const handleCapture = async (payment) => {
    if (!payment?.id) return;
    setActionId(payment.id);
    try {
      await mockCaptureGuestPayment(payment.id);
      toast.success('Payment captured');
      await load(page, activeTab);
    } catch (err) {
      toast.error(err.message || 'Payment capture failed');
    } finally {
      setActionId(null);
    }
  };

  const handleDownloadReceipt = async (payment) => {
    if (!payment?.booking_id) return;
    setActionId(payment.id);
    try {
      const receipt = await fetchGuestPaymentReceipt(payment.booking_id);
      downloadJson(receipt, `receipt-${payment.booking_code || payment.booking_id}.json`);
      toast.success('Receipt downloaded');
    } catch (err) {
      toast.error(err.message || 'Receipt download failed');
    } finally {
      setActionId(null);
    }
  };

  const handleDownloadInvoice = async (payment) => {
    if (!payment?.booking_id) return;
    setActionId(payment.id);
    try {
      const res = await listGuestInvoices({ bookingId: payment.booking_id, limit: 1 });
      const invoice = res?.data?.[0];
      if (!invoice) {
        toast.error('Invoice not available yet.');
        return;
      }
      await openInvoicePdf(invoice.id, null, { asGuest: true, download: true });
    } catch (err) {
      if (err.code === 'POPUP_BLOCKED') {
        toast.error('Popup blocked. Please allow popups to download invoices.');
        return;
      }
      toast.error(err.message || 'Invoice download failed');
    } finally {
      setActionId(null);
    }
  };

  const pageSummary = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit + 1;
    const end = Math.min(pagination.page * pagination.limit, pagination.total || 0);
    if (!pagination.total) return '0 results';
    return `${start}-${end} of ${pagination.total}`;
  }, [pagination]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Payments</p>
          <h2 className="text-2xl font-semibold text-slate-900">Track stays, payments, and invoices.</h2>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{pageSummary}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabChange(tab.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold ${
              activeTab === tab.key ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {error && <div className="px-4 py-3 text-sm text-rose-600">{error}</div>}
        {loading && <div className="px-4 py-3 text-sm text-slate-500">Loading payments...</div>}

        {!loading && !error && items.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500">No payments found for this tab.</div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3">Booking</th>
                  <th className="px-4 py-3">Property</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">{payment.booking_code}</td>
                    <td className="px-4 py-3">
                      <div className="text-slate-900 font-medium">{payment.property_title || 'Property'}</div>
                      <div className="text-xs text-slate-500">{payment.property_location || 'Location TBD'}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {formatAmount(payment.currency, payment.amount, payment.amount_paise)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge value={payment.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDateTime(payment.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => openDetail(payment.id)}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-200 hover:text-emerald-700"
                        >
                          Receipt
                        </button>
                        {canCapturePayment(payment.status) && (
                          <button
                            type="button"
                            onClick={() => handleCapture(payment)}
                            disabled={actionId === payment.id}
                            className="rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 disabled:opacity-50"
                          >
                            Capture payment
                          </button>
                        )}
                        {canRetryPayment(payment.status) && (
                          <button
                            type="button"
                            onClick={() => handleRetry(payment)}
                            disabled={actionId === payment.id}
                            className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 disabled:opacity-50"
                          >
                            Retry payment
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Previous
          </button>
          <div className="text-slate-600">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
            disabled={page >= pagination.totalPages}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            onClick={closeDetail}
            className="absolute inset-0 bg-slate-950/60"
            aria-label="Close payment detail"
          />
          <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Payment detail</p>
                <p className="text-lg font-semibold text-slate-900">{selected?.booking_code || 'Payment'}</p>
              </div>
              <button
                type="button"
                onClick={closeDetail}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600"
              >
                Close
              </button>
            </div>

            {detailLoading && <div className="px-5 py-6 text-sm text-slate-500">Loading details...</div>}

            {!detailLoading && selected && (
              <div className="grid gap-5 px-5 py-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Payment</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">{selected.booking_code}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <StatusBadge value={selected.status} />
                      {selected.booking_payment_status && <StatusBadge value={selected.booking_payment_status} />}
                    </div>
                    <p className="mt-3 text-sm text-slate-600">{formatDateTime(selected.created_at)}</p>
                    <p className="mt-2 text-sm text-slate-600">
                      Reference: {selected.reference || 'N/A'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4 text-sm text-slate-700 space-y-2">
                    <div>
                      <p className="text-xs text-slate-400">Amount</p>
                      <p className="font-semibold">
                        {formatAmount(selected.currency, selected.amount, selected.amount_paise)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Check-in</p>
                      <p className="font-semibold">{formatDate(selected.check_in_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Check-out</p>
                      <p className="font-semibold">{formatDate(selected.check_out_date)}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Property</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">{selected.property_title || 'Property'}</p>
                    <p className="text-sm text-slate-600">{selected.property_location || 'Location TBD'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-400">Actions</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleDownloadReceipt(selected)}
                        disabled={actionId === selected.id}
                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
                      >
                        Download receipt
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadInvoice(selected)}
                        disabled={actionId === selected.id}
                        className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        Download invoice (PDF)
                      </button>
                      {canCapturePayment(selected.status) && (
                        <button
                          type="button"
                          onClick={() => handleCapture(selected)}
                          disabled={actionId === selected.id}
                          className="rounded-lg border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 disabled:opacity-50"
                        >
                          Capture payment
                        </button>
                      )}
                      {canRetryPayment(selected.status) && (
                        <button
                          type="button"
                          onClick={() => handleRetry(selected)}
                          disabled={actionId === selected.id}
                          className="rounded-lg bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 disabled:opacity-50"
                        >
                          Retry payment
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestPayments;
